import { FC, ReactNode, useEffect, useState } from "react";
import { startCase } from "lodash";
import { FilteringOptionsPanel } from "@/ui";
import { OrdersDateFilter, OrdersStatusFilter } from "./filters";
import styles from "./OrdersFilters.module.scss";

/**
 * Orders list filters — shared FilteringOptionsPanel look (Order History),
 * wired to useOrdersData(). Sticky chrome + collapse axis are page-local.
 */
const filterOrder = ["date", "status"];

const filterComponents: Record<string, FC> = {
    date: OrdersDateFilter,
    status: OrdersStatusFilter,
};

const DESKTOP_MEDIA = "(min-width: 1024px)";

export type OrdersFiltersViewProps = {
    sections: { key: string; title: string; content: ReactNode }[];
    collapsed?: boolean;
    defaultCollapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
};

export const OrdersFiltersView: FC<OrdersFiltersViewProps> = ({
    sections,
    collapsed,
    defaultCollapsed,
    onCollapsedChange,
}) => (
    <FilteringOptionsPanel
        className={styles.root}
        collapsed={collapsed}
        collapsible
        defaultCollapsed={defaultCollapsed}
        onCollapsedChange={onCollapsedChange}
        sections={sections}
    />
);

export const OrdersFilters: FC = () => {
    let order = filterOrder;
    if (process.env.REACT_APP_ALGOLIA_ORDERS_FILTER_SORT) {
        order = process.env.REACT_APP_ALGOLIA_ORDERS_FILTER_SORT.split(
            ",",
        ).filter(k => filterComponents[k]);
        if (order.length === 0) order = filterOrder;
    }

    const sections = order
        .map(filter => {
            const Filter = filterComponents[filter];
            if (!Filter) return null;
            return {
                key: filter,
                title: startCase(filter),
                content: <Filter />,
            };
        })
        .filter(Boolean) as OrdersFiltersViewProps["sections"];

    const [collapsed, setCollapsed] = useState(() =>
        typeof window !== "undefined"
            ? !window.matchMedia(DESKTOP_MEDIA).matches
            : true,
    );

    useEffect(() => {
        const mq = window.matchMedia(DESKTOP_MEDIA);
        const sync = (event: MediaQueryListEvent) => {
            /* Crossing the breakpoint resets to the axis default. */
            setCollapsed(!event.matches);
        };
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    return (
        <OrdersFiltersView
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
            sections={sections}
        />
    );
};
